import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Calendar, Building, BookOpen, Tags, ChevronDown, ChevronUp } from 'lucide-react';
import { SearchHighlight } from './SearchHighlight';
import type { Database } from '@shared/types/database';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface SearchResult {
  id: string;
  ecli: string;
  title: string;
  date: string;
  court: string;
  domain: string;
  summary: string;
  relevance: number;
  rechtsgebieden?: Array<{ naam: string }>;
  zaak_types?: Array<{ naam: string }>;
  wetsartikelen?: Array<{ volledige_referentie: string }>;
  themas?: Array<{ naam: string }>;
  // New fields for search highlighting
  relevance_score?: number;
  titel_highlight?: string;
  samenvatting_highlight?: string;
  tekst_highlight?: string;
  // Fuzzy highlighting - words that matched via word_similarity
  fuzzy_matched_words?: string[];
  // Portal identifier (lexzone, lexwoo, lexlabour)
  portal?: Database["public"]["Enums"]["portal_type"];
}

interface SearchResultCardProps {
  result: SearchResult;
  onClick?: () => void;
  searchQuery?: string;
  /** Show summary expanded by default (used on Recent Cases page) */
  initiallyExpanded?: boolean;
}

function SearchResultCardComponent({ result, onClick, searchQuery, initiallyExpanded = false }: SearchResultCardProps) {
  // State for badge collapse/expand
  const [showAllBadges, setShowAllBadges] = useState(false);
  const MAX_VISIBLE_BADGES = 3;

  // State for summary expand/collapse
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(initiallyExpanded);

  // Combine themas and wetsartikelen into a single badge array for collapse/expand logic
  const allBadges = [
    ...(result.themas || []).map((thema, index) => (
      <Badge
        key={`thema-${index}`}
        className="bg-primary/10 text-primary border-primary/20 text-xs"
      >
        <Tags className="h-3 w-3 mr-1" />
        {thema.naam}
      </Badge>
    )),
    ...(result.wetsartikelen || []).map((wetsartikel, index) => (
      <Badge
        key={`wetsartikel-${index}`}
        className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs"
      >
        <BookOpen className="h-3 w-3 mr-1" />
        {wetsartikel.volledige_referentie}
      </Badge>
    ))
  ];

  const visibleBadges = showAllBadges ? allBadges : allBadges.slice(0, MAX_VISIBLE_BADGES);
  const shouldShowToggle = allBadges.length > MAX_VISIBLE_BADGES;

  // Helper to extract rechtsvraag from JSON summary
  const getRechtsvraag = () => {
    try {
      const parsed = JSON.parse(result.summary);
      if (typeof parsed === 'object' && parsed !== null) {
        // Look for rechtsvraag key (case insensitive)
        const rechtsvraagKey = Object.keys(parsed).find(
          key => key.toLowerCase() === 'rechtsvraag'
        );
        if (rechtsvraagKey && parsed[rechtsvraagKey]) {
          return String(parsed[rechtsvraagKey]);
        }
      }
    } catch {
      // Not JSON, return null
    }
    return null;
  };

  const rechtsvraag = getRechtsvraag();

  return (
    <Card
      className="group hover:shadow-xl hover:border-primary/50 transition-all duration-300 cursor-pointer border-border bg-card rounded-xl"
      onClick={onClick}
    >
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-semibold text-primary leading-tight group-hover:text-primary-hover transition-colors break-words overflow-hidden hyphens-auto flex-1">
            <SearchHighlight
              highlightedText={result.titel_highlight}
              plainText={result.title}
              showFullTextWithHighlights={true}
              searchQuery={searchQuery}
              fuzzyMatchedWords={result.fuzzy_matched_words}
            />
          </CardTitle>
          </div>
          <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-muted-foreground font-medium">
            {/* ECLI badge */}
            <span className="bg-muted px-2 py-1 rounded font-mono tracking-wide border border-border">
              {result.ecli}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {result.date}
            </span>
            <span className="flex items-center gap-1">
              <Building className="h-3.5 w-3.5" />
              {result.court}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Rechtsvraag Box - collapsed shows rechtsvraag, expanded shows full summary */}
        {rechtsvraag && (
          <div className="mt-2 bg-muted/50 dark:bg-muted/30 rounded-lg p-3 border border-border/50">
            {!isSummaryExpanded ? (
              // Collapsed: show only rechtsvraag
              <p className="text-sm leading-relaxed line-clamp-3">
                <span className="font-bold block mb-1">Rechtsvraag:</span>
                {rechtsvraag}
              </p>
            ) : (
              // Expanded: show full structured summary
              <div className="text-sm leading-relaxed space-y-2">
                {(() => {
                  const formatKey = (key: string) => {
                    const words = key.split('_');
                    return words
                      .map((word, index) => index === 0
                        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        : word.toLowerCase()
                      )
                      .join(' ');
                  };

                  const normalizeMarkdownText = (text: string) => {
                    return text
                      .split('\n')
                      .map(line => line.trim())
                      .join('\n')
                      .replace(/^>\s*/gm, '')
                      .replace(/\n{3,}/g, '\n\n');
                  };

                  try {
                    const parsed = JSON.parse(result.summary);
                    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                      const entries = Object.entries(parsed)
                        .filter(([_, value]) => value && String(value).trim() !== '');

                      if (entries.length > 0) {
                        return entries.map(([key, value], index) => (
                          <div key={index} className="min-w-0">
                            <span className="font-semibold text-foreground">{formatKey(key)}:</span>{' '}
                            <span className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_li]:my-0 [&_p]:my-2 [&_strong]:font-semibold inline">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {normalizeMarkdownText(String(value))}
                              </ReactMarkdown>
                            </span>
                          </div>
                        ));
                      }
                    }
                  } catch {
                    // Fallback if parsing fails
                  }
                  return <p>{rechtsvraag}</p>;
                })()}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-auto py-1 px-0 text-xs font-medium text-primary hover:text-primary-hover"
              onClick={(e) => {
                e.stopPropagation();
                setIsSummaryExpanded(!isSummaryExpanded);
              }}
              aria-expanded={isSummaryExpanded}
              aria-label={isSummaryExpanded ? "Minder weergeven" : "Volledige samenvatting weergeven"}
            >
              {isSummaryExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Minder weergeven
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Volledige samenvatting
                </>
              )}
            </Button>
          </div>
        )}

        {/* Fallback summary for non-JSON or no rechtsvraag */}
        {!rechtsvraag && (
          <>
            <div className={`mt-2 text-sm text-muted-foreground min-w-0 ${!isSummaryExpanded ? 'line-clamp-3' : ''}`}>
              {(() => {
                const formatKey = (key: string) => {
                  const words = key.split('_');
                  return words
                    .map((word, index) => index === 0
                      ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      : word.toLowerCase()
                    )
                    .join(' ');
                };

                const normalizeMarkdownText = (text: string) => {
                  return text
                    .split('\n')
                    .map(line => line.trim())
                    .join('\n')
                    .replace(/^>\s*/gm, '')
                    .replace(/\n{3,}/g, '\n\n');
                };

                try {
                  const parsed = JSON.parse(result.summary);
                  if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                    const entries = Object.entries(parsed)
                      .filter(([_, value]) => value && String(value).trim() !== '');

                    if (entries.length > 0) {
                      return (
                        <div className="space-y-1 min-w-0">
                          {entries.map(([key, value], index) => (
                            <div key={index} className="min-w-0">
                              <span className="font-semibold text-foreground">{formatKey(key)}:</span>{' '}
                              <span className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_li]:my-0 [&_p]:my-2 [&_strong]:font-semibold inline">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {normalizeMarkdownText(String(value))}
                                </ReactMarkdown>
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                  }
                  return (
                    <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {normalizeMarkdownText(result.summary)}
                      </ReactMarkdown>
                    </div>
                  );
                } catch {
                  return (
                    <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {result.summary}
                      </ReactMarkdown>
                    </div>
                  );
                }
              })()}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-auto py-1 px-0 text-xs font-medium text-primary hover:text-primary-hover"
              onClick={(e) => {
                e.stopPropagation();
                setIsSummaryExpanded(!isSummaryExpanded);
              }}
              aria-expanded={isSummaryExpanded}
              aria-label={isSummaryExpanded ? "Minder weergeven" : "Meer weergeven"}
            >
              {isSummaryExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Minder weergeven
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Meer weergeven
                </>
              )}
            </Button>
          </>
        )}

        {/* Badge section with collapse/expand functionality */}
        {allBadges.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {visibleBadges}

            {shouldShowToggle && (
              <Badge
                variant="outline"
                className="cursor-pointer hover:bg-muted text-xs bg-muted/50 text-muted-foreground border-border"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllBadges(!showAllBadges);
                }}
                role="button"
                aria-expanded={showAllBadges}
                aria-label={showAllBadges ? "Minder tonen" : `Toon ${allBadges.length - MAX_VISIBLE_BADGES} meer`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAllBadges(!showAllBadges);
                  }
                }}
              >
                {showAllBadges ? (
                  'Minder tonen'
                ) : (
                  `+${allBadges.length - MAX_VISIBLE_BADGES} meer`
                )}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Export with Sentry profiler for performance monitoring
export const SearchResultCard = Sentry.withProfiler(SearchResultCardComponent, { name: 'SearchResultCard' });
